import { useEffect, useState } from "react";
import { Sparkles, Loader2, Plus, CalendarPlus } from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

interface Wish {
  id: string;
  text: string;
  created_at: string;
}

/** /wishes - undated aspirations register. Convert a wish to a dated
 *  soft_target (moves into important_dates). */
export function Wishes() {
  const [wishes, setWishes] = useState<Wish[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState<string | null>(null);
  const [date, setDate] = useState("");

  const load = () => {
    fetch("/api/wishes", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { wishes: [] }))
      .then((j) => setWishes(j.wishes ?? []))
      .catch(() => setWishes([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await fetch("/api/wishes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ text: text.trim() }),
      });
      setText("");
      load();
    } finally {
      setBusy(false);
    }
  };

  const convert = async (id: string) => {
    setBusy(true);
    try {
      await fetch(`/api/wishes/${encodeURIComponent(id)}/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ date }),
      });
      setConverting(null);
      setDate("");
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Explore", href: "/explore" }, { label: "Library", href: "/explore/library" }, { label: "Wishes" }]} />
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Wishes
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Undated aspirations. Give one a date to make it a soft target.
        </p>
      </header>

      <TeachingBanner id="wishes_overview" />

      <div className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="I want to…"
          className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={add}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-2"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : wishes.length === 0 ? (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-6 text-sm text-slate-500 dark:text-slate-400">
          No wishes yet. Add one above.
        </div>
      ) : (
        <ul className="space-y-2">
          {wishes.map((w) => (
            <li
              key={w.id}
              className="glass rounded-lg border border-slate-200/70 dark:border-slate-700/50 p-3 space-y-2"
            >
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1 text-sm text-slate-800 dark:text-slate-100">
                  {w.text}
                </div>
                <button
                  type="button"
                  onClick={() => setConverting(converting === w.id ? null : w.id)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 dark:text-sky-300"
                >
                  <CalendarPlus className="w-3.5 h-3.5" /> Convert
                </button>
              </div>
              {converting === w.id && (
                <div className="flex gap-2 pl-7">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    disabled={busy || !date}
                    onClick={() => convert(w.id)}
                    className="rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1"
                  >
                    To soft target
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
